import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Search, Eye, Edit, Trash2, CheckCircle, XCircle, Star, StarOff, Power, PowerOff, Shield, ShieldCheck, Car, HelpCircle, Calendar } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes.constants';
import vehicleService from '../../../services/vehicleService';
import vehicleTypeService from '../../../services/vehicleTypeService';
import VehicleDetailsModal from '../../../components/admin/vehicles/VehicleDetailsModal';
import VehicleDeletionActionModal from '../../../components/admin/vehicles/VehicleDeletionActionModal';
import AvailabilityModal from '../../../components/agency/AvailabilityModal';
import { useAuth } from '../../../context/AuthContext';
import { getImageUrl } from '../../../utils/imageUtils';

// Flag global pour éviter les doubles appels même avec React.StrictMode
// Utiliser un Map pour suivre par route/instance
const loadStateByRoute = new Map();

/**
 * Vehicle List Page
 * Admin can view and manage all vehicles with status filters
 * Vendor can view only their own vehicles
 */
const VehicleList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isVendor = user?.role === 'vendor';
  
  // Créer une clé unique pour cette instance de navigation
  // Utiliser useMemo pour éviter les recréations inutiles
  const routeKey = React.useMemo(() => `${location.pathname}-${location.search}`, [location.pathname, location.search]);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isLoadingRef = useRef(false);
  const mountedRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const filtersInitializedRef = useRef(false);
  const initialLoadInProgressRef = useRef(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  });

  // Filters
  const [filters, setFilters] = useState({
    isActive: '',
    isVerified: '',
    isFeatured: '',
    category: '',
    owner: '',
  });
  
  // Data for filters
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);

  // Data for category filter (dynamic)
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Modal state
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  
  // Availability modal state
  const [availabilityModalVehicleId, setAvailabilityModalVehicleId] = useState(null);
  const [availabilityModalVehicleTitle, setAvailabilityModalVehicleTitle] = useState('');
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  
  // Deletion modal state
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [selectedVehicleIdForDeletion, setSelectedVehicleIdForDeletion] = useState(null);
  
  // Tooltip state for rejection message
  const [hoveredRejectionId, setHoveredRejectionId] = useState(null);

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = () => {
    if (selectedRows.length === vehicles.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(vehicles.map((_, index) => index));
    }
  };

  const handleSelectRow = (index) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter(i => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };

  // Load vehicles from API
  const loadVehicles = useCallback(async () => {
    // Éviter les appels multiples simultanés (protection globale + locale)
    const routeState = loadStateByRoute.get(routeKey) || { done: false, inProgress: false };
    if (isLoadingRef.current || routeState.inProgress) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      loadStateByRoute.set(routeKey, { done: routeState.done, inProgress: true });
      setIsLoading(true);
      
      const params = {
        page: currentPage,
        limit: entriesPerPage,
        search: searchTerm,
        sortBy: sortConfig.key || 'createdAt',
        sortOrder: sortConfig.direction || 'desc',
        ...(filters.isActive !== '' && { isActive: filters.isActive === 'true' }),
        ...(filters.isVerified !== '' && { isVerified: filters.isVerified === 'true' }),
        ...(filters.isFeatured !== '' && { isFeatured: filters.isFeatured === 'true' }),
        // Filtre backend via `type` (ID vehicleType)
        ...(filters.category !== '' && { type: filters.category }),
        // Si c'est un vendor, filtrer automatiquement par son ID
        // Note: Le backend force aussi ce filtre côté serveur pour la sécurité
        ...(isVendor && (user?._id || user?.id) ? { owner: user._id || user.id } : (filters.owner !== '' && { owner: filters.owner })),
      };

      const response = await vehicleService.getAllVehicles(params);

      if (response.success) {
        // La structure courante de l'API est du type :
        // { success: true, data: { vehicles: [...], pagination: {...} } }
        // mais on garde une compatibilité avec l'ancien format (data = tableau directement)
        const data = response.data || {};
        const vehiclesArray = data.vehicles || data || [];

        setVehicles(Array.isArray(vehiclesArray) ? vehiclesArray : []);

        const paginationData = response.pagination || data.pagination;
        if (paginationData) {
          setPagination(paginationData);
        }
      } else {
        setVehicles([]);
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
      setVehicles([]);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      const currentState = loadStateByRoute.get(routeKey) || { done: false, inProgress: false };
      loadStateByRoute.set(routeKey, { done: true, inProgress: false });
    }
  }, [currentPage, entriesPerPage, searchTerm, sortConfig, filters, isVendor, user?._id, user?.id, routeKey]);

  // Load vehicles on initial mount only - Protection contre React.StrictMode avec flag par route
  useEffect(() => {
    // Vérifier l'état de chargement pour cette route spécifique
    const routeState = loadStateByRoute.get(routeKey) || { done: false, inProgress: false };
    
    // Si déjà chargé pour cette route ET qu'on a des véhicules, ne pas recharger
    if (routeState.done && vehicles.length > 0 && !isLoading) {
      initialLoadDoneRef.current = true;
      mountedRef.current = true;
      return;
    }
    
    // Si un chargement est en cours pour cette route, attendre
    if (routeState.inProgress && !initialLoadDoneRef.current) {
      return;
    }
    
    // Marquer IMMÉDIATEMENT pour cette route pour éviter les doubles appels
    loadStateByRoute.set(routeKey, { done: false, inProgress: true });
    initialLoadDoneRef.current = true;
    mountedRef.current = true;
    isLoadingRef.current = true;
    initialLoadInProgressRef.current = true;
    
    // Appel direct sans dépendance pour éviter les re-renders
    const loadInitial = async () => {
      try {
        setIsLoading(true);
        const params = {
          page: 1,
          limit: entriesPerPage,
          search: '',
          sortBy: 'createdAt',
          sortOrder: 'desc',
          ...(isVendor && (user?._id || user?.id) ? { owner: user._id || user.id } : {}),
        };

        const response = await vehicleService.getAllVehicles(params);

        if (response.success) {
          // Même logique que dans loadVehicles pour supporter
          // la nouvelle structure { data: { vehicles: [...], pagination } }
          const data = response.data || {};
          const vehiclesArray = data.vehicles || data || [];

          setVehicles(Array.isArray(vehiclesArray) ? vehiclesArray : []);

          const paginationData = response.pagination || data.pagination;
          if (paginationData) {
            setPagination(paginationData);
          }
        } else {
          setVehicles([]);
        }
      } catch (error) {
        console.error('Error loading vehicles:', error);
        setVehicles([]);
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
        loadStateByRoute.set(routeKey, { done: true, inProgress: false });
        initialLoadInProgressRef.current = false;
        // Réactiver le troisième useEffect après le chargement initial avec un délai
        setTimeout(() => {
          filtersInitializedRef.current = true;
        }, 100);
      }
    };
    
    loadInitial();
    
    // Cleanup: réinitialiser les refs quand le composant se démonte
    return () => {
      initialLoadDoneRef.current = false;
      mountedRef.current = false;
      // Ne pas supprimer de loadStateByRoute car on veut garder l'état par route
      // Mais on peut nettoyer les anciennes routes après un certain temps si nécessaire
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  // Debounce search term - only trigger after initial mount
  useEffect(() => {
    if (initialLoadInProgressRef.current) {
      return;
    }
    
    if (!initialLoadDoneRef.current) {
      return;
    }
    
    // Si searchTerm est vide et qu'on vient juste de finir le chargement initial, ne pas recharger
    if (searchTerm === '' && !filtersInitializedRef.current) {
      return;
    }
    
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadVehicles();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Load vehicles when filters, pagination, or sort change - only trigger after initial mount
  useEffect(() => {
    if (initialLoadInProgressRef.current) {
      return;
    }
    
    const currentRouteState = loadStateByRoute.get(routeKey) || { done: false, inProgress: false };
    if (!currentRouteState.done || !initialLoadDoneRef.current) {
      if (!filtersInitializedRef.current) {
        filtersInitializedRef.current = true;
      }
      return;
    }
    
    if (!filtersInitializedRef.current) {
      return;
    }
    
    if (isLoadingRef.current || currentRouteState.inProgress) {
      return;
    }
    
    loadVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, entriesPerPage, sortConfig, filters.isActive, filters.isVerified, filters.isFeatured, filters.category, filters.owner]);

  // Load agencies for filter
  useEffect(() => {
    const loadAgencies = async () => {
      try {
        setLoadingAgencies(true);
        const token = localStorage.getItem('accessToken');
        if (!token) return;

        const response = await fetch(`${ROUTES.API_BASE_URL}/admin/agencies?status=APPROVED`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAgencies(data.data || []);
          }
        }
      } catch (error) {
        console.error('Error loading agencies:', error);
      } finally {
        setLoadingAgencies(false);
      }
    };

    loadAgencies();
  }, []);

  // Load categories for filter (same API as AddVehicle.jsx)
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);

        // Utiliser l'API publique pour les vendors, l'API admin pour les admins
        const response = isVendor
          ? await vehicleTypeService.getPublicTypes()
          : await vehicleTypeService.getAllTypes({
              page: 1,
              limit: 100,
              status: 'active',
            });

        const typesArray = response.data || response || [];

        if (Array.isArray(typesArray) && typesArray.length > 0) {
          // Pour filtrer côté backend /vehicles :
          // dropdown => vehicleType._id (et on envoie ce "value" en paramètre `type`)
          setCategories(
            typesArray.map((type) => ({
              value: type._id || type.id,
              label: type.name,
            })),
          );
        } else {
          setCategories([]);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, [isVendor]);

  // Détecter le state pour ouvrir automatiquement le modal de suppression
  useEffect(() => {
    if (location.state?.openDeletionModal && location.state?.vehicleId) {
      setSelectedVehicleIdForDeletion(location.state.vehicleId);
      setIsDeletionModalOpen(true);
      // Nettoyer le state pour éviter de réouvrir le modal à chaque rendu
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleToggleActive = async (vehicleId, currentStatus) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir ${currentStatus ? 'désactiver' : 'activer'} ce véhicule ?`)) {
      return;
    }

    try {
      await vehicleService.toggleActive(vehicleId);
      await loadVehicles();
    } catch (error) {
      console.error('Error toggling active status:', error);
      alert(error.message || 'Erreur lors de la modification du statut');
    }
  };

  const handleToggleVerified = async (vehicleId, currentStatus, hasRejectionReason, vehicle) => {
    try {
      // Si le véhicule est refusé, réinitialiser le refus pour passer à "Non vérifié"
      if (hasRejectionReason) {
        if (!window.confirm('Voulez-vous réinitialiser le statut de refus et passer ce véhicule à "Non vérifié" ?')) {
          return;
        }
        
        // Réinitialiser le refus (passe automatiquement à "Non vérifié")
        const resetResponse = await vehicleService.resetVehicleRejection(vehicleId);
        if (!resetResponse.success) {
          alert(resetResponse.message || 'Erreur lors de la réinitialisation du statut de refus');
          return;
        }
        
        // Mise à jour optimiste : mettre à jour immédiatement le véhicule dans la liste
        setVehicles(prevVehicles => 
          prevVehicles.map(v => 
            v._id === vehicleId 
              ? { ...v, rejectionReason: null, isVerified: false }
              : v
          )
        );
      } else if (currentStatus) {
        // Si vérifié, passer à "Non vérifié"
        if (!window.confirm('Voulez-vous déverifier ce véhicule (passer à "Non vérifié") ?')) {
          return;
        }
        await vehicleService.toggleVerified(vehicleId);
      } else {
        // Si non vérifié, passer à "Vérifié"
        if (!window.confirm('Voulez-vous vérifier ce véhicule (passer à "Vérifié") ?')) {
          return;
        }
        await vehicleService.toggleVerified(vehicleId);
      }
      
      // Désactiver temporairement le troisième useEffect pour éviter le double appel
      filtersInitializedRef.current = false;
      
      // Attendre un peu pour que les états se mettent à jour et forcer le rechargement
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Forcer le rechargement en réinitialisant le flag de chargement
      isLoadingRef.current = false;
      const currentState = loadStateByRoute.get(routeKey) || { done: false, inProgress: false };
      loadStateByRoute.set(routeKey, { done: false, inProgress: false });
      
      await loadVehicles();
      
      // Réactiver le troisième useEffect après le chargement
      setTimeout(() => {
        filtersInitializedRef.current = true;
      }, 200);
    } catch (error) {
      console.error('Error toggling verified status:', error);
      alert(error.message || 'Erreur lors de la modification de la vérification');
    }
  };

  const handleViewDetails = (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    setIsDetailsModalOpen(true);
  };

  const handleOpenAvailabilityModal = (vehicleId, vehicleTitle) => {
    setAvailabilityModalVehicleId(vehicleId);
    setAvailabilityModalVehicleTitle(vehicleTitle);
    setIsAvailabilityModalOpen(true);
  };

  const handleCloseAvailabilityModal = () => {
    setIsAvailabilityModalOpen(false);
    setAvailabilityModalVehicleId(null);
    setAvailabilityModalVehicleTitle('');
  };

  const handleVerifyFromModal = async (vehicleId) => {
    try {
      await vehicleService.toggleVerified(vehicleId);
      await loadVehicles();
    } catch (error) {
      console.error('Error verifying vehicle:', error);
      alert(error.message || 'Erreur lors de la vérification');
    }
  };

  const handleToggleFeatured = async (vehicleId, currentStatus) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir ${currentStatus ? 'retirer de la mise en avant' : 'mettre en avant'} ce véhicule ?`)) {
      return;
    }

    try {
      await vehicleService.toggleFeatured(vehicleId);
      await loadVehicles();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert(error.message || 'Erreur lors de la modification de la mise en avant');
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ? Cette action est irréversible.')) {
      return;
    }

    try {
      await vehicleService.deleteVehicle(vehicleId);
      await loadVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      alert(error.message || 'Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (vehicle) => {
    const badges = [];
    
    if (vehicle.isActive) {
      badges.push(
        <span key="active" className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Actif
        </span>
      );
    } else {
      badges.push(
        <span key="inactive" className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
          Inactif
        </span>
      );
    }

    // Vérifier si le véhicule a été rejeté
    if (vehicle.rejectionReason) {
      badges.push(
        <div key="rejected" className="flex items-center gap-1.5 relative">
          <span className="px-2.5 py-1 text-xs font-semibold bg-red-500 text-white rounded-full flex items-center gap-1.5 shadow-sm border border-red-600">
            <XCircle className="w-3.5 h-3.5" />
            Refusé
          </span>
          <div 
            className="relative"
            onMouseEnter={() => setHoveredRejectionId(vehicle._id)}
            onMouseLeave={() => setHoveredRejectionId(null)}
          >
            <HelpCircle className="w-4 h-4 text-red-500 cursor-help" />
            {hoveredRejectionId === vehicle._id && (
              <div className="absolute left-0 top-6 z-50 w-80 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl border border-gray-700">
                <div className="font-semibold mb-2 text-red-400">Motif du refus :</div>
                <div className="whitespace-pre-wrap">{vehicle.rejectionReason.message}</div>
                <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45 border-l border-t border-gray-700"></div>
              </div>
            )}
          </div>
        </div>
      );
    } else if (vehicle.isVerified) {
      badges.push(
        <span key="verified" className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" />
          Vérifié
        </span>
      );
    } else {
      badges.push(
        <span key="not-verified" className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Non vérifié
        </span>
      );
    }

    if (vehicle.isFeatured) {
      badges.push(
        <span key="featured" className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">
          <Star className="w-3 h-3" />
          En vedette
        </span>
      );
    }

    return <div className="flex flex-wrap gap-1">{badges}</div>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{isVendor ? 'Mes véhicules' : 'Liste des véhicules'}</h1>
            <p className="text-gray-500 mt-1">{isVendor ? 'Gérer vos véhicules' : 'Gérer tous les véhicules du système'}</p>
          </div>
          <button
            onClick={() => navigate(isVendor ? ROUTES.VENDOR_VEHICLES_ADD : ROUTES.ADMIN_VEHICLES_ADD)}
            className="px-4 py-2 bg-[#27489E] text-white rounded-lg hover:bg-[#1e3a7a] transition flex items-center gap-2"
          >
            <span>+</span>
            <span>Ajouter un véhicule</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className={`grid grid-cols-1 ${isVendor ? 'md:grid-cols-5' : 'md:grid-cols-6'} gap-4`}>
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={isVendor ? "Rechercher un véhicule..." : "Rechercher..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E]"
              />
            </div>

            {/* Active Filter */}
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E]"
            >
              <option value="">Tous les statuts</option>
              <option value="true">Actif</option>
              <option value="false">Inactif</option>
            </select>

            {/* Verified Filter */}
            <select
              value={filters.isVerified}
              onChange={(e) => setFilters({ ...filters, isVerified: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E]"
            >
              <option value="">Toutes les vérifications</option>
              <option value="true">Vérifié</option>
              <option value="false">Non vérifié</option>
            </select>

            {/* Featured Filter */}
            <select
              value={filters.isFeatured}
              onChange={(e) => setFilters({ ...filters, isFeatured: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E]"
            >
              <option value="">Toutes les mises en avant</option>
              <option value="true">En vedette</option>
              <option value="false">Non en vedette</option>
            </select>

            {/* Category Filter */}
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E]"
              disabled={loadingCategories}
            >
              <option value="">Toutes les catégories</option>
              {loadingCategories ? (
                <option value="" disabled>
                  Chargement...
                </option>
              ) : (
                categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))
              )}
            </select>

            {/* Agency Filter - Only approved partners - Hidden for vendors */}
            {!isVendor && (
              <select
                value={filters.owner}
                onChange={(e) => setFilters({ ...filters, owner: e.target.value })}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E]"
                disabled={loadingAgencies}
              >
                <option value="">Tous les partenaires</option>
                {agencies
                  .filter(agency => agency.vendorStatus === 'APPROVED')
                  .map((agency) => (
                    <option key={agency._id} value={agency._id}>
                      {agency.vendorDetails?.companyName || agency.email}
                    </option>
                  ))}
              </select>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : vehicles.length === 0 ? (
            <div className="p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Car className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isVendor ? 'Aucun véhicule pour le moment' : 'Aucun véhicule trouvé'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {isVendor 
                    ? 'Commencez par ajouter votre premier véhicule pour le proposer à la location.'
                    : 'Aucun véhicule ne correspond aux filtres sélectionnés.'}
                </p>
                {isVendor && (
                  <button
                    onClick={() => navigate(ROUTES.VENDOR_VEHICLES_ADD)}
                    className="px-6 py-3 bg-[#27489E] text-white rounded-lg hover:bg-[#1e3a7a] transition font-medium flex items-center gap-2 mx-auto"
                  >
                    <span>+</span>
                    <span>Ajouter mon premier véhicule</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedRows.length === vehicles.length && vehicles.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-[#27489E] focus:ring-[#27489E]"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Véhicule
                      </th>
                      {/* Colonne Propriétaire - Masquée pour les vendors */}
                      {!isVendor && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Propriétaire
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Localisation
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prix/jour
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statuts
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vehicles.map((vehicle, index) => (
                      <tr 
                        key={vehicle._id} 
                        className={`transition ${
                          vehicle.rejectionReason 
                            ? 'bg-red-50 hover:bg-red-100' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(index)}
                            onChange={() => handleSelectRow(index)}
                            className="rounded border-gray-300 text-[#27489E] focus:ring-[#27489E]"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {vehicle.images && vehicle.images.length > 0 ? (
                              <img
                                src={getImageUrl(vehicle.images[0]) || 'https://via.placeholder.com/64x64?text=No+Image'}
                                alt={vehicle.title || 'Véhicule'}
                                className="w-16 h-16 object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/64x64?text=No+Image';
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                                <span className="text-xs text-gray-400">Pas d'image</span>
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-900">{vehicle.title || 'Sans nom'}</div>
                              <div className="text-sm text-gray-500">
                                {vehicle.specs?.brand?.name} {vehicle.specs?.model?.name}
                              </div>
                              <div className="text-xs text-gray-400">{vehicle.licencePlateNumber || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        {/* Colonne Propriétaire - Masquée pour les vendors */}
                        {!isVendor && (
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {vehicle.owner?.fullName || vehicle.owner?.email || 'N/A'}
                            </div>
                            {vehicle.owner?.vendorDetails?.companyName && (
                              <div className="text-xs text-gray-500">{vehicle.owner.vendorDetails.companyName}</div>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {vehicle.location?.city || vehicle.vehicleLocation?.cityName || 'N/A'}
                          </div>
                          {vehicle.vehicleLocation?.country && (
                            <div className="text-xs text-gray-500">{vehicle.vehicleLocation.country}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {vehicle.pricing?.basePrice ? `${vehicle.pricing.basePrice} ${vehicle.pricing?.currency || 'MAD'}` : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(vehicle)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewDetails(vehicle._id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Voir les détails"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {/* Availability Calendar Button - Visible for vendors and admins */}
                            <button
                              onClick={() => handleOpenAvailabilityModal(vehicle._id, vehicle.title || 'Véhicule')}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                              title="Gérer les disponibilités"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(vehicle._id, vehicle.isActive)}
                              className={`p-2 rounded-lg transition ${
                                vehicle.isActive
                                  ? 'text-green-600 hover:bg-green-50'
                                  : 'text-gray-400 hover:bg-gray-50'
                              }`}
                              title={vehicle.isActive ? 'Désactiver' : 'Activer'}
                            >
                              {vehicle.isActive ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                            </button>
                            {/* Actions réservées à l'admin */}
                            {!isVendor && (
                              <>
                                <button
                                  onClick={() => handleToggleVerified(vehicle._id, vehicle.isVerified, !!vehicle.rejectionReason, vehicle)}
                                  className={`p-2 rounded-lg transition ${
                                    vehicle.rejectionReason
                                      ? 'text-red-600 hover:bg-red-50'
                                      : vehicle.isVerified
                                      ? 'text-blue-600 hover:bg-blue-50'
                                      : 'text-yellow-600 hover:bg-yellow-50'
                                  }`}
                                  title={
                                    vehicle.rejectionReason 
                                      ? 'Réinitialiser le refus et passer à "Non vérifié"' 
                                      : (vehicle.isVerified ? 'Déverifier (passer à "Non vérifié")' : 'Vérifier (passer à "Vérifié")')
                                  }
                                >
                                  {vehicle.isVerified ? <ShieldCheck className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => handleToggleFeatured(vehicle._id, vehicle.isFeatured)}
                                  className={`p-2 rounded-lg transition ${
                                    vehicle.isFeatured
                                      ? 'text-purple-600 hover:bg-purple-50'
                                      : 'text-gray-400 hover:bg-gray-50'
                                  }`}
                                  title={vehicle.isFeatured ? 'Retirer de la vedette' : 'Mettre en vedette'}
                                >
                                  {vehicle.isFeatured ? <Star className="w-4 h-4" /> : <StarOff className="w-4 h-4" />}
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => navigate(isVendor ? ROUTES.VENDOR_VEHICLES_EDIT_BY_ID(vehicle._id) : ROUTES.ADMIN_VEHICLES_EDIT_BY_ID(vehicle._id))}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(vehicle._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Afficher</span>
                  <select
                    value={entriesPerPage}
                    onChange={(e) => {
                      setEntriesPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#27489E]"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-700">
                    sur {pagination.totalItems} résultats
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Précédent
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {pagination.currentPage} sur {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                    disabled={currentPage === pagination.totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Vehicle Details Modal */}
        <VehicleDetailsModal
          vehicleId={selectedVehicleId}
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedVehicleId(null);
          }}
          onVerify={handleVerifyFromModal}
          onReject={async (vehicleId) => {
            // Recharger la liste après un refus
            await loadVehicles();
          }}
          onResetRejection={async (vehicleId) => {
            // Recharger la liste après une réinitialisation du refus
            await loadVehicles();
          }}
        />

        {/* Availability Modal */}
        <AvailabilityModal
          isOpen={isAvailabilityModalOpen}
          onClose={handleCloseAvailabilityModal}
          vehicleId={availabilityModalVehicleId}
          vehicleTitle={availabilityModalVehicleTitle}
        />

        {/* Vehicle Deletion Action Modal */}
        <VehicleDeletionActionModal
          isOpen={isDeletionModalOpen}
          onClose={() => {
            setIsDeletionModalOpen(false);
            setSelectedVehicleIdForDeletion(null);
          }}
          vehicleId={selectedVehicleIdForDeletion}
          onSuccess={async (message) => {
            // Recharger la liste après une action de suppression
            await loadVehicles();
          }}
        />
      </div>
    </DashboardLayout>
  );
};

export default VehicleList;
